import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { OPPORTUNITIES_QUERY_STRING } from 'shared/constants/QUERY_STRING';
import { IProjectAttributes } from 'shared/interfaces/IProject';
import { useAppSelector } from 'shared/redux/store';
import { opportunitiesServices } from 'shared/services/opportunitiesServices';
import { talentService } from 'shared/services/talentService';
import { ls } from 'shared/utils/ls';
import { IOpporunitiesContext, TModalContent } from '../OpportunitiesContext.types';
import { opportunitiesDefaultStates } from '../OpportunitiesDefaultStates';

const { getOpportunities, removeProjects, applyToRole: applyToRoleService } = opportunitiesServices();
const { getTalentProfileIds, getMe, updateMe } = talentService();

export const useOpportunitiesData = (
  setOpportunities: Dispatch<SetStateAction<IProjectAttributes[]>>,
  opportunities: IProjectAttributes[],
  setNotInterestedDataState: Dispatch<SetStateAction<IProjectAttributes[]>>,
  setAppliedDataState: Dispatch<SetStateAction<IProjectAttributes[]>>,
  openOpportunityModal: (content: TModalContent) => void,
) => {
  const { setLS } = ls();
  const { user: userState } = useAppSelector((state) => state);
  const subdomain = window.location.hostname.split('.')[0];
  const isJob = subdomain === 'jobs';
  const { project_id } = useParams<{ project_id: string }>();
  const { data: profiles } = getTalentProfileIds(!isJob);
  const { mutate: removeProjectsMutation } = removeProjects();
  const { mutate: applyToRoleMutation } = applyToRoleService();
  const { mutate: updateMeMutation } = updateMe();
  const [filters, setFilterState] = useState(opportunitiesDefaultStates.filters);
  const [applicationData, setApplicationData] = useState({
    profile_id: '',
    casting_role_id: '',
  });
  const [appliedRoles, setAppliedRoles] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading: opportunitiesFetching,
    isFetchingNextPage: opportunitiesRefetching,
  } = getOpportunities(filters, {
    enabled: !project_id,
  });

  //useMemo to get me preferences
  const mePreferences = useMemo(() => {
    return userState?.user?.attributes.preferences;
  }, [userState]);

  const opportunitiesData = useMemo(() => {
    // //get last index of pages and concat all opportunities
    // const lastPage = data?.pages[data.pages.length - 1];

    // return lastPage?.data ? lastPage?.data.opportunities_connection.nodes : [];

    return data ? [].concat(...data.pages.map((page: any) => page.data.opportunities_connection.nodes)) : [];
  }, [data]);

  const setFilters = useCallback(
    async (newFilters: Partial<IOpporunitiesContext['filters']>) => {
      // await queryClient.invalidateQueries({
      //   predicate: (query) => {
      //     return query.queryKey.includes(OPPORTUNITIES_QUERY_STRING.OPPORTUNITY_LIST);
      //   },
      // });
      if (filters.tab_filter !== 'new') setOpportunities([]);
      setFilterState((prevFilters) => {
        if (!isJob && mePreferences)
          updateMeMutation({
            preferences: {
              ...mePreferences,
              opportunities_filter: {
                ...prevFilters,
                ...newFilters,
                project_ids: [],
                role_ids: [],
              },
            },
          });
        return {
          ...prevFilters,
          ...newFilters,
        };
      });
      // await queryClient.invalidateQueries(
      //   [
      //     isJob
      //       ? OPPORTUNITIES_QUERY_STRING.OPPORTUNITIES_PUBLIC_AGGREGATES
      //       : OPPORTUNITIES_QUERY_STRING.OPPORTUNITIES_TAB_AGGREGATES,
      //     filters,
      //   ],
      //   {
      //     refetchInactive: true,
      //   },
      // );
    },

    [setOpportunities, isJob],
  );

  const setOnlyFilters = useCallback((newFilters: Partial<IOpporunitiesContext['filters']>) => {
    setFilterState({
      ...newFilters,
    } as IOpporunitiesContext['filters']);
  }, []);

  const fetchMoreOpportunities = useCallback(() => {
    if (hasNextPage && !opportunitiesRefetching) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, opportunitiesRefetching]);

  const removeProject = useCallback(
    (id: string) => {
      setOpportunities((prevOpportunities) => prevOpportunities.filter((opportunity) => opportunity.id !== id));
      removeProjectsMutation(
        {
          ids: [id],
        },
        {
          onSuccess: () => {
            const opportunity = opportunities.find((opportunity) => opportunity.id === id);

            if (opportunity) {
              setNotInterestedDataState((prevOpportunities) => prevOpportunities.concat(opportunity));
            }
            queryClient.invalidateQueries(OPPORTUNITIES_QUERY_STRING.OPPORTUNITIES_TAB_AGGREGATES);
          },
        },
      );
    },
    [setOpportunities, removeProjectsMutation, queryClient, opportunities, setNotInterestedDataState],
  );

  const continueApplication = useCallback(
    (declined: boolean) => {
      const { casting_role_id, profile_id } = applicationData;
      if (declined) {
        setApplicationData({
          profile_id: '',
          casting_role_id: '',
        });
      } else {
        applyToRoleMutation(
          {
            casting_role_id,
            //TODO : for now just use the first non-represented profile but needs to check private profile
            profile_id,
          },
          {
            onSuccess: async () => {
              await queryClient.invalidateQueries(OPPORTUNITIES_QUERY_STRING.OPPORTUNITIES_TAB_AGGREGATES);
              const opportunity = opportunities.find((opportunity) =>
                opportunity.casting_roles.some((role) => role.id === casting_role_id),
              );

              setAppliedRoles((prev) => [...prev, casting_role_id]);
              if (opportunity) {
                //include only the role within the opportunity with same id as casting_role_id
                const updatedOpportunity = {
                  ...opportunity,
                  casting_roles: opportunity.casting_roles.filter((role) => role.id === casting_role_id),
                  opportunities_aggregates: {
                    ...opportunity.opportunities_aggregates,
                    filtered_casting_roles_on_notice_board_count: 1,
                  },
                };
                await queryClient.resetQueries([OPPORTUNITIES_QUERY_STRING.PROJECT_ROLES, opportunity.id]);
                setAppliedDataState((prevOpportunities) => {
                  //check if the opportunity is already in the applied list

                  if (prevOpportunities.some((o) => o.id === updatedOpportunity.id)) {
                    return prevOpportunities.map((o) => {
                      if (o.id === updatedOpportunity.id) {
                        //copy the casting_roles from the updated opportunity
                        return {
                          ...o,
                          casting_roles: [...o.casting_roles, ...updatedOpportunity.casting_roles],
                          opportunities_aggregates: {
                            ...o.opportunities_aggregates,
                            filtered_casting_roles_on_notice_board_count:
                              o.opportunities_aggregates.filtered_casting_roles_on_notice_board_count + 1,
                          },
                        };
                      }
                      return o;
                    });
                  }

                  return prevOpportunities.concat(updatedOpportunity);
                });

                setApplicationData({
                  profile_id: '',
                  casting_role_id: '',
                });
              }
            },
            onError: (error) => {
              setApplicationData({
                profile_id: '',
                casting_role_id: '',
              });
              console.error(error);
            },
          },
        );
      }
    },
    [applyToRoleMutation, applicationData, queryClient, opportunities, setAppliedDataState],
  );

  const applyToRole = useCallback(
    async (casting_role_id: string, project_id: string, company_name: string, role_name: string) => {
      return new Promise<boolean>((resolve, reject) => {
        if (isJob) {
          setLS('pending_job_application', JSON.stringify({ casting_role_id, project_id, company_name, role_name }));
          openOpportunityModal('login_register');
          return resolve(false);
        }
        const talentProfileIds =
          profiles?.data?.profiles
            .filter((profile: any) => profile.representation_type === 'non_represented')
            .map((profile: any) => profile.id) || [];

        if (talentProfileIds?.length) {
          setApplicationData({
            casting_role_id,
            profile_id: talentProfileIds[0],
          });
          openOpportunityModal('size_card');
          resolve(true);
        } else {
          openOpportunityModal('no_non_representation');
          console.log('No non-represented profiles found');
          return resolve(false);
        }
      });
    },
    [profiles, openOpportunityModal, isJob, setLS],
  );

  useEffect(() => {
    setFilterState((prevFilters) => {
      return (
        mePreferences?.opportunities_filter || {
          ...opportunitiesDefaultStates.filters,
          countries:
            userState?.user?.attributes.country &&
            ['United States', 'Australia', 'New Zealand'].includes(userState?.user?.attributes.country)
              ? userState?.user?.attributes.country
              : 'United States',
        }
      );
    });
  }, [mePreferences, opportunitiesDefaultStates.filters]);

  useEffect(() => {
    if (isJob) return;
    if (filters && mePreferences) {
      updateMeMutation({
        preferences: { ...mePreferences, opportunities_filter: filters },
      });
    }
  }, [filters, updateMeMutation, isJob, mePreferences]);

  useEffect(() => {
    //staggered fetching
    if (opportunities.length < 10 && hasNextPage) {
      fetchMoreOpportunities();
    }
  }, [opportunities, hasNextPage, fetchMoreOpportunities]);

  useEffect(() => {
    if (!opportunitiesData?.length) return;

    // setOpportunities((prevOpportunities) => {
    //   const newOpportunities = opportunitiesData.pages
    //     .map((page: any) => page.data.opportunities_connection.nodes)
    //     .flat()
    //     .filter((opportunity: any) => !prevOpportunities.some((o) => o.id === opportunity.id));
    //   return prevOpportunities.concat(newOpportunities);
    // });
    // setOpportunities((prevOpportunities) => prevOpportunities.concat(opportunitiesData));
    setOpportunities([...opportunitiesData]);
  }, [opportunitiesData, setOpportunities]);

  return {
    setOnlyFilters,
    continueApplication,
    applyToRole,
    filters,
    setFilters,
    opportunitiesFetching,
    fetchMoreOpportunities,
    opportunitiesRefetching,
    removeProject,
    setOpportunities,
    appliedRoles,
    applicationData,
  };
};
